import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTutorial } from '../contexts/TutorialContext';
import { makeAuthenticatedRequest, handleApiError } from '../utils/api';
import {
  SkillTreeData, SkillNode, LearnSkillResponse,
  TalentPointTransaction, PassiveAbilityDefinition, SkillEffectData,
  EquippedSkill,
} from '../types/skillTree';
import SkillTooltip from './SkillTooltip';
import SkillDetailsPanel from './SkillDetailsPanel';
import SkillHotbar from './SkillHotbar';
import PassiveBonuses from './PassiveBonuses';
import './SkillTree.css';

interface SkillTreeProps {
  characterId: number;
  onNavigateToSkills?: () => void;
}

// Mirrors SkillTreeController.RespecGoldCostPerPoint - shown for the confirm-dialog estimate only;
// the server always computes and validates the real charge.
const RESPEC_GOLD_COST_PER_POINT = 10;

interface SkillNodePosition {
  skillId: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

// Pure helpers used across multiple sub-components — passed down as props to avoid re-importing.
const getSkillIcon = (skill: SkillNode): string => {
  const iconMap: { [key: string]: string } = {
    Passive: '🛡️', Active: '⚔️', Mastery: '👑', Signature: '✨', Utility: '🔧',
  };
  return iconMap[skill.skillCategory] || '❓';
};

const parseSkillEffects = (effectsJson: string): SkillEffectData | null => {
  try { return JSON.parse(effectsJson); } catch { return null; }
};

const SkillTree: React.FC<SkillTreeProps> = ({ characterId, onNavigateToSkills }) => {
  const { token } = useAuth();
  const { checkAndStartTutorial } = useTutorial();
  const [skillTreeData, setSkillTreeData] = useState<SkillTreeData | null>(null);
  const [selectedSkill, setSelectedSkill] = useState<SkillNode | null>(null);
  const [talentHistory, setTalentHistory] = useState<TalentPointTransaction[]>([]);
  const [passiveDefinitions, setPassiveDefinitions] = useState<PassiveAbilityDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [learningSkill, setLearningSkill] = useState<number | null>(null);
  const [resetting, setResetting] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [skillPositions, setSkillPositions] = useState<Map<number, SkillNodePosition>>(new Map());
  const [hoveredSkill, setHoveredSkill] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('All');
  const [equippedSkills, setEquippedSkills] = useState<EquippedSkill[]>([]);
  const [draggedSkill, setDraggedSkill] = useState<SkillNode | null>(null);
  const skillRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  useEffect(() => {
    loadSkillTree();
    loadTalentHistory();
    loadPassiveDefinitions();
    loadEquippedSkills();
  }, [characterId]);

  useEffect(() => {
    if (skillTreeData && !loading) {
      checkAndStartTutorial('skill-tree-basics');
    }
  }, [skillTreeData, loading, checkAndStartTutorial]);

  useEffect(() => {
    if (!skillTreeData) return;
    const calculatePositions = () => {
      const positions = new Map<number, SkillNodePosition>();
      const containerRect = document.querySelector('.skill-tree-tiers')?.getBoundingClientRect();
      if (!containerRect) return;
      skillRefs.current.forEach((element, skillId) => {
        const rect = element.getBoundingClientRect();
        positions.set(skillId, {
          skillId,
          x: rect.left - containerRect.left + rect.width / 2,
          y: rect.top - containerRect.top + rect.height / 2,
          width: rect.width,
          height: rect.height,
        });
      });
      setSkillPositions(positions);
    };
    calculatePositions();
    window.addEventListener('resize', calculatePositions);
    return () => window.removeEventListener('resize', calculatePositions);
  }, [skillTreeData]);

  // Suppress unused-variable warning — token used by makeAuthenticatedRequest internally.
  void token;

  const loadSkillTree = async () => {
    try {
      setLoading(true);
      const response = await makeAuthenticatedRequest(`/skilltree/character/${characterId}`);
      if (!response.ok) throw new Error('Failed to load skill tree');
      const data = await response.json();
      setSkillTreeData(data);
      setError(null);
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const loadTalentHistory = async () => {
    try {
      const response = await makeAuthenticatedRequest(`/skilltree/character/${characterId}/talent-history`);
      if (response.ok) setTalentHistory(await response.json());
    } catch { /* non-critical */ }
  };

  const loadPassiveDefinitions = async () => {
    try {
      const response = await makeAuthenticatedRequest('/skilltree/passive-abilities');
      if (response.ok) setPassiveDefinitions(await response.json());
    } catch { /* non-critical */ }
  };

  const loadEquippedSkills = async () => {
    try {
      const response = await makeAuthenticatedRequest(`/skillhotbar/character/${characterId}`);
      if (response.ok) setEquippedSkills(await response.json());
    } catch { /* non-critical */ }
  };

  const equipSkillToHotbar = async (skillNodeId: number, hotbarSlot: number) => {
    try {
      const response = await makeAuthenticatedRequest('/skillhotbar/equip', {
        method: 'POST',
        body: JSON.stringify({ characterId, skillNodeId, hotbarSlot }),
      });
      if (response.ok) {
        await loadEquippedSkills();
        setSuccessMessage('Skill equipped successfully!');
        setTimeout(() => setSuccessMessage(null), 2000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to equip skill');
        setTimeout(() => setError(null), 3000);
      }
    } catch {
      setError('Failed to equip skill');
      setTimeout(() => setError(null), 3000);
    }
  };

  const unequipSkillFromHotbar = async (hotbarSlot: number) => {
    try {
      const response = await makeAuthenticatedRequest('/skillhotbar/unequip', {
        method: 'POST',
        body: JSON.stringify({ characterId, hotbarSlot }),
      });
      if (response.ok) await loadEquippedSkills();
    } catch { /* non-critical */ }
  };

  const learnSkill = async (skillNodeId: number) => {
    if (!skillTreeData || learningSkill) return;
    try {
      setLearningSkill(skillNodeId);
      const response = await makeAuthenticatedRequest(
        `/skilltree/character/${characterId}/learn/${skillNodeId}`,
        { method: 'POST' }
      );
      if (!response.ok) {
        let msg = 'Failed to learn skill';
        try { msg = (await response.json()).error || msg; } catch { msg = await response.text() || msg; }
        throw new Error(msg);
      }
      const result: LearnSkillResponse = await response.json();
      if (result.success) {
        await loadSkillTree();
        await loadTalentHistory();
        setSelectedSkill(null);
      }
    } catch (err) {
      setError(handleApiError(err).message);
    } finally {
      setLearningSkill(null);
    }
  };

  const resetSkills = async () => {
    if (!skillTreeData || resetting) return;
    try {
      setResetting(true);
      setShowResetConfirm(false);
      const response = await makeAuthenticatedRequest(
        `/skilltree/character/${characterId}/reset`,
        { method: 'POST' }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reset skills');
      }
      const result = await response.json();
      if (result.success) {
        await loadSkillTree();
        await loadTalentHistory();
        await loadEquippedSkills();
        setSelectedSkill(null);
        setSuccessMessage(
          `Successfully reset ${result.skillsReset} skills and refunded ${result.talentPointsRefunded} talent points (-${result.goldSpent} gold)`
        );
        setTimeout(() => setSuccessMessage(null), 3000);
        onNavigateToSkills?.();
      }
    } catch (err) {
      setError(handleApiError(err).message);
    } finally {
      setResetting(false);
    }
  };

  const getSkillNodeClass = (skill: SkillNode): string => {
    if (skill.isLearned) return 'skill-node learned';
    if (skill.canLearn) return 'skill-node can-learn';
    if (skill.requiredLevel > (skillTreeData?.characterLevel || 1)) return 'skill-node locked';
    return 'skill-node cannot-learn';
  };

  const getTierLabel = (tier: number): string => {
    const labels: { [key: number]: string } = {
      1: 'Tier I - Foundation', 2: 'Tier II - Specialization',
      3: 'Tier III - Advanced', 4: 'Tier IV - Master', 5: 'Tier V - Legendary',
    };
    return labels[tier] || `Tier ${tier}`;
  };

  const handleSkillHover = (skillId: number, event: React.MouseEvent<HTMLDivElement>) => {
    setHoveredSkill(skillId);
    const rect = event.currentTarget.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    setTooltipPosition({ x: rect.left + scrollLeft + rect.width / 2, y: rect.top + scrollTop - 10 });
  };

  const getAvailableCategories = (): string[] => {
    if (!skillTreeData) return [];
    const categories = new Set<string>();
    skillTreeData.availableSkills.forEach(s => categories.add(s.skillCategory));
    return ['All', ...Array.from(categories).sort()];
  };

  const getCategoryIcon = (category: string): string => {
    const iconMap: { [key: string]: string } = {
      All: '🌟', Passive: '🛡️', Active: '⚔️', Mastery: '👑', Signature: '✨', Utility: '🔧',
    };
    return iconMap[category] || '❓';
  };

  const renderSkillConnections = () => {
    if (!skillTreeData || skillPositions.size === 0) return null;
    return skillTreeData.availableSkills.flatMap(skill => {
      if (!skill.prerequisiteSkillId) return [];
      const fromPos = skillPositions.get(skill.prerequisiteSkillId);
      const toPos = skillPositions.get(skill.id);
      if (!fromPos || !toPos) return [];
      const prereq = skillTreeData.availableSkills.find(s => s.id === skill.prerequisiteSkillId);
      const isActive = prereq?.isLearned && skill.isLearned;
      const isAvailable = prereq?.isLearned && skill.canLearn;
      const isHighlighted = hoveredSkill === skill.id || hoveredSkill === skill.prerequisiteSkillId;
      let strokeColor = '#4a5568';
      let strokeWidth = isHighlighted ? 3 : 2;
      let strokeDasharray = '5,5';
      if (isActive) { strokeColor = '#48bb78'; strokeDasharray = 'none'; strokeWidth = isHighlighted ? 4 : 3; }
      else if (isAvailable) { strokeColor = '#4299e1'; strokeDasharray = 'none'; }
      return [(
        <line
          key={`connection-${skill.prerequisiteSkillId}-${skill.id}`}
          x1={fromPos.x} y1={fromPos.y} x2={toPos.x} y2={toPos.y}
          stroke={strokeColor} strokeWidth={strokeWidth} strokeDasharray={strokeDasharray}
          opacity={isHighlighted ? 1 : 0.6} className="skill-connection"
        />
      )];
    });
  };

  if (loading) return <div className="loading-spinner">Loading skill tree...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!skillTreeData) return <div className="error-message">No skill tree data available</div>;

  if (skillTreeData.availableSkills.length === 0) {
    return (
      <div className="skill-tree-empty-state">
        <div className="empty-state-icon">🌳</div>
        <h2>Skill Tree Not Yet Available</h2>
        <p>The skill tree system is ready, but no skills have been configured yet.</p>
        <p className="empty-state-info">
          Skills need to be seeded into the database first. Contact an administrator or use the admin panel to seed skills.
        </p>
        <div className="empty-state-hint">
          <strong>For Administrators:</strong>
          <p>Use the endpoint: POST /api/skilltree/admin/seed-skills</p>
        </div>
      </div>
    );
  }

  const filteredSkills = categoryFilter === 'All'
    ? skillTreeData.availableSkills
    : skillTreeData.availableSkills.filter(s => s.skillCategory === categoryFilter);

  const skillsByTier: { [tier: number]: SkillNode[] } = {};
  filteredSkills.forEach(skill => {
    (skillsByTier[skill.treeTier] ??= []).push(skill);
  });
  Object.values(skillsByTier).forEach(tier => tier.sort((a, b) => a.treePosition - b.treePosition));

  const hoveredSkillObj = hoveredSkill ? skillTreeData.availableSkills.find(s => s.id === hoveredSkill) : null;

  return (
    <div className="skill-tree-container">
      {/* Tooltip — rendered at document level to avoid clip overflow */}
      {hoveredSkillObj && tooltipPosition && (
        <SkillTooltip
          skill={hoveredSkillObj}
          position={tooltipPosition}
          skillTreeData={skillTreeData}
          passiveDefinitions={passiveDefinitions}
          getSkillIcon={getSkillIcon}
          parseSkillEffects={parseSkillEffects}
        />
      )}

      {successMessage && <div className="success-notification">{successMessage}</div>}

      <div className="skill-tree-main">
        <div className="skill-tree-header">
          <div className="skill-tree-title">{skillTreeData.characterClass} Skill Tree</div>
          <div className="talent-points-display">{skillTreeData.talentPoints.available} Talent Points</div>
        </div>

        <SkillHotbar
          equippedSkills={equippedSkills}
          getSkillIcon={getSkillIcon}
          onDragOver={e => e.preventDefault()}
          onDrop={(e, slot) => { e.preventDefault(); if (draggedSkill) { equipSkillToHotbar(draggedSkill.id, slot); setDraggedSkill(null); } }}
          onUnequip={unequipSkillFromHotbar}
        />

        {/* Skill grid with SVG connections overlay */}
        <div className="skill-tree-tiers">
          <svg
            className="skill-connections-svg"
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0 }}
          >
            {renderSkillConnections()}
          </svg>

          {Object.entries(skillsByTier)
            .sort(([a], [b]) => parseInt(a) - parseInt(b))
            .map(([tier, skills]) => (
              <div key={tier} className="skill-tier-container">
                <div className="tier-label">{getTierLabel(parseInt(tier))}</div>
                <div className="skill-tier-row">
                  {skills.map(skill => (
                    <div
                      key={skill.id}
                      ref={el => { if (el) skillRefs.current.set(skill.id, el); }}
                      className={`${getSkillNodeClass(skill)} ${selectedSkill?.id === skill.id ? 'selected' : ''} ${skill.isActive && skill.isLearned ? 'draggable' : ''}`}
                      draggable={skill.isActive && skill.isLearned}
                      onClick={() => {
                        if (skill.canLearn && !skill.isLearned) learnSkill(skill.id);
                        else setSelectedSkill(skill);
                      }}
                      onMouseEnter={e => handleSkillHover(skill.id, e)}
                      onMouseLeave={() => { setHoveredSkill(null); setTooltipPosition(null); }}
                      onDragStart={() => { if (skill.isActive && skill.isLearned) setDraggedSkill(skill); }}
                      onDragEnd={() => setDraggedSkill(null)}
                    >
                      <div className="skill-icon">{getSkillIcon(skill)}</div>
                      <div className="skill-name">{skill.name}</div>
                      <div className="skill-category-badge">{skill.skillCategory}</div>
                      <div className="skill-cost">{skill.talentPointCost} TP</div>
                      <div className="skill-requirement">Lv {skill.requiredLevel}</div>
                      {skill.isLearned && <div className="learned-checkmark">✓</div>}
                      {skill.isLearned && (() => {
                        const ls = skillTreeData.learnedSkills.find(l => l.skillNodeId === skill.id);
                        return ls && ls.skillLevel > 0
                          ? <div className="skill-level-badge" title={`${ls.skillXP} / ${ls.xpToNextLevel} XP`}>Lv {ls.skillLevel}</div>
                          : null;
                      })()}
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>

        {selectedSkill && (
          <SkillDetailsPanel
            selectedSkill={selectedSkill}
            skillTreeData={skillTreeData}
            passiveDefinitions={passiveDefinitions}
            learningSkill={learningSkill}
            onLearnSkill={learnSkill}
            parseSkillEffects={parseSkillEffects}
          />
        )}
      </div>

      <div className="skill-tree-sidebar">
        {/* Category filters */}
        <div className="category-filters skill-category-filters">
          <div className="category-filters-label">Filter by Category:</div>
          <div className="category-filter-buttons">
            {getAvailableCategories().map(category => (
              <button
                key={category}
                className={`category-filter-button ${categoryFilter === category ? 'active' : ''}`}
                onClick={() => setCategoryFilter(category)}
              >
                <span className="category-filter-icon">{getCategoryIcon(category)}</span>
                <span className="category-filter-text">{category}</span>
              </button>
            ))}
          </div>
        </div>

        {skillTreeData.learnedSkills.length > 0 && (
          <div className="reset-skills-section">
            <button
              className="reset-skills-button"
              onClick={() => setShowResetConfirm(true)}
              disabled={resetting}
            >
              {resetting ? 'Resetting...' : 'Reset All Skills'}
            </button>
          </div>
        )}

        <PassiveBonuses
          passiveBonuses={skillTreeData.passiveBonuses}
          passiveDefinitions={passiveDefinitions}
        />

        {talentHistory.length > 0 && (
          <div className="talent-history-section">
            <div className="talent-history-title">Talent Point History</div>
            {talentHistory.slice(0, 10).map(transaction => (
              <div
                key={transaction.id}
                className={`talent-transaction ${transaction.pointChange > 0 ? 'positive' : 'negative'}`}
              >
                <div>
                  <div className={`talent-transaction-points ${transaction.pointChange > 0 ? 'positive' : 'negative'}`}>
                    {transaction.pointChange > 0 ? '+' : ''}{transaction.pointChange}
                  </div>
                  <div className="talent-transaction-source">{transaction.source}</div>
                </div>
                <div>{transaction.relatedSkillName || transaction.description}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showResetConfirm && (
        <div className="modal-overlay" onClick={() => setShowResetConfirm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">Reset All Skills?</div>
            <div className="modal-body">
              <p>Are you sure you want to reset all learned skills?</p>
              <p>You will refund <strong>{skillTreeData.talentPoints.totalSpent} talent points</strong> and lose all {skillTreeData.learnedSkills.length} learned skills.</p>
              <p>This will cost <strong>{skillTreeData.talentPoints.totalSpent * RESPEC_GOLD_COST_PER_POINT} gold</strong>.</p>
              <p className="modal-warning">This action cannot be undone!</p>
            </div>
            <div className="modal-actions">
              <button className="modal-button modal-button-cancel" onClick={() => setShowResetConfirm(false)}>Cancel</button>
              <button className="modal-button modal-button-confirm" onClick={resetSkills}>Reset Skills</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkillTree;
